/**
 * Shared helpers for Root Cause Analysis views (shutdown list, downtime history, etc.).
 * Row shapes follow `getAllShutdownEventsForAsset` / by-asset list DTOs.
 */

/**
 * Numeric hours from a shutdown row for severity, dots, and duration text.
 * @param {{ DowntimeHours?: number|null|string }} event - Full shutdown list row; only `DowntimeHours` is read (missing → 0).
 * @returns {number}
 */
export function shutdownListEventHours(event) {
  if (
    event.DowntimeHours != null
    && !Number.isNaN(Number(event.DowntimeHours))
  ) {
    return Number(event.DowntimeHours);
  }
  return 0;
}

/**
 * True when the row has a numeric `DowntimeHours` (show duration pill/label); false for ongoing / unset.
 * @param {{ DowntimeHours?: number|null|string }} event - Full shutdown list row; only `DowntimeHours` is read.
 * @returns {boolean}
 */
export function hasDowntimeHours(event) {
  return (
    event.DowntimeHours != null
    && event.DowntimeHours !== ""
    && !Number.isNaN(Number(event.DowntimeHours))
  );
}

/**
 * Round fractional downtime hours for display (whole number if within 0.05 of an integer, otherwise one decimal).
 * Shared by shutdown list duration text, downtime history pills, and summary totals.
 * @param {number} hours
 * @returns {number}
 */
export function roundHoursForDisplay(hours) {
  const h = Number(hours) || 0;
  return Math.abs(h - Math.round(h)) < 0.05
    ? Math.round(h)
    : Math.round(h * 10) / 10;
}

/**
 * Formatting function for displaying some statistical values.
 * @param {unknown} n
 */
export function formatDetailOneDecimal(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  return x.toFixed(1);
}

/**
 * Returns the display label for a measurement row.
 * Prefers `TagDescription`; falls back to `Tag ${AssetTagId}` when missing/blank.
 *
 * @param {{ TagDescription?: string, AssetTagId?: string|number } & Record<string, unknown>} row
 * @returns {string}
 */
export function getMeasurementLabel(row) {
  return String(row.TagDescription ?? "").trim() || `Tag ${row.AssetTagId}`;
}

/**
 * Normalize free-text filter input for case-insensitive matching.
 *
 * @param {string|undefined|null} value
 * @returns {string}
 */
export function normalizeMeasurementFilter(value) {
  return String(value ?? "").trim().toLowerCase();
}

/**
 * Returns true when `label` matches both section and overall filters.
 *
 * @param {string} label
 * @param {{
 *   sectionFilterText?: string,
 *   overallFilterText?: string,
 * }} filters
 * @returns {boolean}
 */
export function measurementLabelMatchesFilters(label, filters) {
  const labelLower = label.toLowerCase();
  const sectionQuery = normalizeMeasurementFilter(filters.sectionFilterText);
  const overallQuery = normalizeMeasurementFilter(filters.overallFilterText);
  const sectionMatches = !sectionQuery || labelLower.includes(sectionQuery);
  const overallMatches = !overallQuery || labelLower.includes(overallQuery);
  return sectionMatches && overallMatches;
}

/** Default decimal places for RCA numeric draft inputs (e.g. drift window settings). */
export const RCA_DECIMAL_INPUT_DECIMALS = 2;

/**
 * @param {number} n
 * @param {number} [decimals]
 */
export function roundRcaDecimalInput(n, decimals = RCA_DECIMAL_INPUT_DECIMALS) {
  if (!Number.isFinite(n)) return n;
  const factor = 10 ** decimals;
  return Math.round(n * factor) / factor;
}

/**
 * @param {number} n
 * @param {number} [decimals]
 */
export function formatRcaDecimalInput(n, decimals = RCA_DECIMAL_INPUT_DECIMALS) {
  return Number.isFinite(n) ? n.toFixed(decimals) : "";
}

/**
 * @param {string} value
 */
export function parseRcaDecimalInput(value) {
  if (value.trim() === "") return NaN;
  const n = Number(value);
  return Number.isFinite(n) ? n : NaN;
}

/**
 * Whole number when near an integer, otherwise fixed decimals (for labels).
 * @param {number} n
 * @param {number} [decimals]
 */
export function formatRcaDecimalLabel(n, decimals = RCA_DECIMAL_INPUT_DECIMALS) {
  return Math.abs(n - Math.round(n)) < 0.005
    ? String(Math.round(n))
    : n.toFixed(decimals);
}

/**
 * @param {string} draft
 * @param {(value: string) => void} setDraft
 * @param {number} [decimals]
 */
export function blurRcaDecimalDraft(
  draft,
  setDraft,
  decimals = RCA_DECIMAL_INPUT_DECIMALS
) {
  const n = parseRcaDecimalInput(draft);
  setDraft(Number.isFinite(n) ? formatRcaDecimalInput(n, decimals) : "");
}

/**
 * @param {string} draft
 * @param {(value: number) => void} setValue
 * @param {(value: string) => void} setDraft
 * @param {(n: number) => boolean} isValid
 * @param {number} [decimals]
 */
export function commitRcaDecimalDraft(
  draft,
  setValue,
  setDraft,
  isValid,
  decimals = RCA_DECIMAL_INPUT_DECIMALS
) {
  const n = parseRcaDecimalInput(draft);
  if (!Number.isFinite(n) || !isValid(n)) return;
  const rounded = roundRcaDecimalInput(n, decimals);
  setValue(rounded);
  setDraft(formatRcaDecimalInput(rounded, decimals));
}
